import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Car, Ambulance, Home, ArrowRight, Clock, Shield, Star,
  AlertCircle, CheckCircle, MapPin, Navigation, User, Loader,
  History
} from "lucide-react";
import { getPassengerData } from "../../passengerAuth";
import "./Passenger.css";

const services = [
  {
    id: "ride",
    type: "ride",
    icon: <Car size={28} />,
    title: "Non-Emergency Service",
    desc: "Book a safe medical transport. We'll show you available vehicles with estimated prices for your route.",
    path: "/passenger/ride",
    badge: "Available 24/7",
    badgeColor: "#1d4ed8",
  },
  {
    id: "emergency",
    type: "emergency",
    icon: <Ambulance size={28} />,
    title: "Emergency Response",
    desc: "Immediate emergency medical assistance with smart triage and dispatch.",
    path: "/passenger/emergency",
    badge: "Urgent",
    badgeColor: "#dc2626",
  },
  // ── Home Care temporarily disabled ──
  // {
  //   id: "homecare",
  //   type: "homecare",
  //   icon: <Home size={28} />,
  //   title: "Home Care",
  //   desc: "Professional medical home care services dispatched to your door.",
  //   path: "/passenger/homecare",
  //   badge: "Scheduled",
  //   badgeColor: "#16a34a",
  // },
];

const stats = [
  // { icon: <Clock size={17} />, label: "Avg Response", value: "< 8 min" },
  { icon: <Shield size={17} />, label: "Verified Drivers", value: "100%" },
  { icon: <Star size={17} />, label: "Service Rating", value: "4.9 / 5" },
  { icon: <AlertCircle size={17} />, label: "Emergency Line", value: "9117" },
];

const ACTIVE_STATUSES = ["Active", "Accepted", "Started", "InProgress", "ArrivedAtPickup"];

function ActiveTripBanner({ trip, navigate }) {
  const isEmergency = trip?.isEmergency;
  const driver = trip?.Driver || trip?.driver;

  const getStatusLabel = (s) => {
    const map = {
      Active: "Searching for driver…",
      Accepted: "Driver accepted — on the way",
      Started: "Ride in progress",
      InProgress: "Ride in progress",
      ArrivedAtPickup: "Driver arrived at pickup",
    };
    return map[s] || s;
  };

  return (
    <div
      className="pax-active-trip-banner"
      style={{
        background: isEmergency
          ? "linear-gradient(135deg, #fef2f2, #fee2e2)"
          : "linear-gradient(135deg, #f0fdf4, #dcfce7)",
        borderColor: isEmergency ? "#fca5a5" : "#86efac",
      }}
    >
      <div className="pax-active-trip-left">
        <div
          className="pax-active-trip-icon"
          style={{
            background: isEmergency ? "#fca5a530" : "#86efac30",
            color: isEmergency ? "#dc2626" : "#16a34a",
          }}
        >
          {isEmergency ? <Ambulance size={24} /> : <Car size={24} />}
        </div>
        <div>
          <div className="pax-active-trip-label">
            <span
              className="pax-active-dot"
              style={{ background: isEmergency ? "#dc2626" : "#16a34a" }}
            />
            Active Trip
          </div>
          <div className="pax-active-trip-status">{getStatusLabel(trip?.status)}</div>
          {driver && (
            <div className="pax-active-trip-driver">
              <User size={12} />
              {driver.firstName} {driver.lastName}
              {driver.phoneNumber && ` · ${driver.phoneNumber}`}
            </div>
          )}
          {trip?.pickUpPlace && (
            <div className="pax-active-trip-route">
              <MapPin size={11} color="#94a3b8" />
              {trip.pickUpPlace}
              {trip.destinationPlace && (
                <>
                  <ArrowRight size={10} color="#94a3b8" />
                  {trip.destinationPlace}
                </>
              )}
            </div>
          )}
        </div>
      </div>
      <button
        className="pax-active-trip-btn"
        style={{
          background: isEmergency ? "#dc2626" : "#16a34a",
        }}
        onClick={() => navigate(isEmergency ? "/passenger/emergency" : "/passenger/ride")}
      >
        View Trip <ArrowRight size={14} />
      </button>
    </div>
  );
}

export default function PassengerHome() {
  const navigate = useNavigate();
  const passenger = getPassengerData();
  const firstName = passenger?.firstName || "Passenger";
  const passengerId = passenger?.id;

  const [activeTrip, setActiveTrip] = useState(null);
  const [loadingTrip, setLoadingTrip] = useState(true);

  useEffect(() => {
    if (!passengerId) {
      setLoadingTrip(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/passenger/${passengerId}/active-trip`
        );
        if (res.data?.hasActiveTrip && res.data?.trip) {
          setActiveTrip(res.data.trip);
        }
      } catch {
        // silent fail
      } finally {
        setLoadingTrip(false);
      }
    })();
  }, [passengerId]);

  return (
    <div className="pax-home-wrapper">
      {/* HERO */}
      <div className="pax-home-hero">
        <div className="pax-hero-content">
          <div className="pax-hero-tag">
            <span className="pax-hero-dot" />
            Services Active
          </div>
          <h1 className="pax-hero-title">Hello, {firstName} 👋</h1>
          <p className="pax-hero-subtitle">
            What service do you need today? Select a service option below to get started.
          </p>
        </div>

        <div style={{ display: "flex", gap: 20, marginTop: 24, flexWrap: "wrap" }}>
          {stats.map((s) => (
            <div
              key={s.label}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "rgba(255,255,255,0.1)", borderRadius: 10,
                padding: "8px 14px", backdropFilter: "blur(6px)",
              }}
            >
              <span style={{ color: "rgba(255,255,255,0.7)" }}>{s.icon}</span>
              <div>
                <div style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {s.label}
                </div>
                <div style={{ fontSize: "0.9rem", fontWeight: 700, color: "#fff" }}>{s.value}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ACTIVE TRIP BANNER */}
      {loadingTrip ? null : activeTrip ? (
        <ActiveTripBanner trip={activeTrip} navigate={navigate} />
      ) : null}

      {/* SERVICE CARDS */}
      <div>
        <div
          style={{
            marginBottom: 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <h2
            style={{
              margin: 0, fontSize: "1.05rem", fontWeight: 700,
              color: "#0f172a", letterSpacing: "-0.01em",
            }}
          >
            Choose a Service
          </h2>
          <button
            className="pax-btn-secondary"
            style={{ fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 6 }}
            onClick={() => navigate("/passenger/trips")}
          >
            <History size={14} /> View Trip History
          </button>
        </div>

        <div className="pax-services-grid">
          {services.map((svc) => (
            <div
              key={svc.id}
              className={`pax-service-card ${svc.type}`}
              onClick={() => navigate(svc.path)}
              id={`pax-service-${svc.id}`}
            >
              <div className="pax-service-icon-wrap">{svc.icon}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                <h3 className="pax-service-title">{svc.title}</h3>
                <span
                  style={{
                    fontSize: "0.7rem", fontWeight: 700, padding: "3px 8px",
                    borderRadius: "100px", textTransform: "uppercase", letterSpacing: "0.05em",
                    background:
                      svc.type === "ride" ? "#eff6ff"
                        : svc.type === "emergency" ? "#fef2f2"
                          : "#f0fdf4",
                    color: svc.badgeColor,
                  }}
                >
                  {svc.badge}
                </span>
              </div>
              <p className="pax-service-desc">{svc.desc}</p>
              <button className="pax-service-btn">
                Get Started <ArrowRight size={15} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* BOTTOM TIPS */}
      <div
        style={{
          background: "#ffffff", border: "1.5px solid #e2e8f0",
          borderRadius: 16, padding: "20px 24px",
          display: "flex", alignItems: "center", gap: 14,
        }}
      >
        <div
          style={{
            width: 44, height: 44, background: "#fef2f2", borderRadius: 12,
            display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          <AlertCircle size={22} color="#dc2626" />
        </div>
        <div>
          <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.95rem" }}>
            To reach a public ambulance, call{" "}
            <span style={{ color: "#dc2626" }}>939</span> or{" "}
            <span style={{ color: "#dc2626" }}>907</span>
          </div>
          <div style={{ fontSize: "0.82rem", color: "#64748b", marginTop: 2 }}>
            For life-threatening emergencies, call the national hotline directly or use the Emergency service above.
          </div>
        </div>
      </div>
    </div>
  );
}
